/**
 * Marketing landing page with a hero section, animated type sequence,
 * platform icons, key feature cards, statistics counters, and a CTA.
 *
 * @module HomePage
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid2 as Grid,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Chip,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LanguageIcon from '@mui/icons-material/Language';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SpeedIcon from '@mui/icons-material/Speed';
import BrushIcon from '@mui/icons-material/Brush';
import AppleIcon from '@mui/icons-material/Apple';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { DARK_BLUE, GREEN } from '../theme';
import api from '../api';
import {
  FadeInSection,
  StaggerContainer,
  StaggerItem,
  FloatingOrb,
  AnimatedNumber,
  GradientBorderCard,
  PageTransition,
} from '../components/AnimatedComponents';

/** Shape of a media item. */
interface ShowcaseMedia {
  url: string;
  type: 'image' | 'video';
  filename: string;
}

/** Shape of a translation for a single language. */
interface ShowcaseTranslation {
  name: string;
  description: string;
  short_description: string;
}

/** Shape of a showcase app as returned by the API. */
interface ShowcaseApp {
  id: string;
  name: string;
  description: string;
  short_description: string;
  translations?: Record<string, ShowcaseTranslation>;
  platforms: string[];
  screenshots: string[];
  media: ShowcaseMedia[];
  tags: string[];
}

/** Resolve the best translation for a showcase app given a language code. */
function getTranslation(app: ShowcaseApp, lang: string): ShowcaseTranslation {
  const tr = app.translations;
  if (tr) {
    if (tr[lang]) return tr[lang];
    if (tr['nl']) return tr['nl'];
    if (tr['en']) return tr['en'];
  }
  return { name: app.name, description: app.description, short_description: app.short_description };
}

/** Platform icon entries displayed in the hero section. */
const platforms = [
  { icon: <PhoneAndroidIcon sx={{ fontSize: 40 }} />, name: 'Android' },
  { icon: <AppleIcon sx={{ fontSize: 40 }} />, name: 'iOS' },
  { icon: <DesktopWindowsIcon sx={{ fontSize: 40 }} />, name: 'Windows' },
  { icon: <AppleIcon sx={{ fontSize: 40 }} />, name: 'macOS' },
  { icon: <DesktopWindowsIcon sx={{ fontSize: 40 }} />, name: 'Linux' },
  { icon: <LanguageIcon sx={{ fontSize: 40 }} />, name: 'Web' },
];

/** Home page component with hero, features, stats, and call-to-action sections. */
export default function HomePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showcaseApps, setShowcaseApps] = useState<ShowcaseApp[]>([]);

  useEffect(() => {
    api.get('/api/showcase/').then((res) => setShowcaseApps(res.data)).catch(() => {});
  }, []);

  const features = [
    {
      icon: <SmartToyIcon sx={{ fontSize: 52 }} />,
      title: t('home.featureAi'),
      desc: t('home.featureAiDesc'),
      gradient: `linear-gradient(135deg, ${GREEN}, #2d9c1c)`,
      slug: 'ai-powered-software',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 52 }} />,
      title: t('home.featureQuick'),
      desc: t('home.featureQuickDesc'),
      gradient: `linear-gradient(135deg, ${DARK_BLUE}, #004a5e)`,
      slug: 'quick-and-solid-development',
    },
    {
      icon: <BrushIcon sx={{ fontSize: 52 }} />,
      title: t('home.featureCustom'),
      desc: t('home.featureCustomDesc'),
      gradient: `linear-gradient(135deg, #6b46c1, #805ad5)`,
      slug: 'custom-built-software',
    },
  ];

  const stats = [
    { value: 6, suffix: '', label: t('home.statPlatforms'), icon: <RocketLaunchIcon /> },
    { value: 99, suffix: '%', label: t('home.statSatisfaction'), icon: <GroupsIcon /> },
    { value: 100, suffix: '%', label: t('home.statSecure'), icon: <SecurityIcon /> },
  ];

  return (
    <PageTransition>
      <SEO
        canonical="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: t('seo.defaultTitle'),
          description: t('seo.defaultDescription'),
          url: 'https://www.booge-it.com/',
        }}
      />
      <Box>
        {/* Hero */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${DARK_BLUE} 0%, #003d52 50%, #004a5e 100%)`,
            color: 'white',
            py: { xs: 10, md: 16 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative elements */}
          <FloatingOrb size={500} color={GREEN} top={-150} right={-100} />
          <FloatingOrb size={350} color="#4FC3F7" bottom={-100} left={-80} animationDelay="3s" />
          <FloatingOrb size={200} color={GREEN} top="60%" left="20%" animationDelay="5s" />

          {/* Grid pattern overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `radial-gradient(circle at 1px 1px, ${alpha('#ffffff', 0.03)} 1px, transparent 0)`,
              backgroundSize: '40px 40px',
              pointerEvents: 'none',
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.8rem' },
                      mb: 2,
                      lineHeight: 1.1,
                    }}
                  >
                    {t('home.heroTitle')}{' '}
                    <Box
                      component="span"
                      sx={{
                        color: GREEN,
                        display: 'inline-grid',
                        verticalAlign: 'top',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        minWidth: 0,
                      }}
                    >
                      {/* Hidden placeholders to reserve space for the tallest animation string */}
                      {[t('home.heroAnim1'), t('home.heroAnim2'), t('home.heroAnim3'), t('home.heroAnim4')].map((text, i) => (
                        <Box
                          key={i}
                          component="span"
                          sx={{ gridArea: '1 / 1', visibility: 'hidden' }}
                          aria-hidden="true"
                        >
                          {text}
                        </Box>
                      ))}
                      <Box
                        component="span"
                        sx={{
                          gridArea: '1 / 1',
                          textDecoration: 'underline',
                          textDecorationColor: GREEN,
                          textDecorationThickness: 4,
                          textUnderlineOffset: 6,
                        }}
                      >
                        <TypeAnimation
                          key={i18n.language}
                          sequence={[
                            t('home.heroAnim1'),
                            3000,
                            t('home.heroAnim2'),
                            3000,
                            t('home.heroAnim3'),
                            3000,
                            t('home.heroAnim4'),
                            3000,
                          ]}
                          wrapper="span"
                          speed={40}
                          repeat={Infinity}
                        />
                      </Box>
                    </Box>
                  </Typography>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 5,
                      opacity: 0.85,
                      fontWeight: 300,
                      maxWidth: 550,
                      lineHeight: 1.6,
                    }}
                  >
                    {t('home.heroDesc')}
                  </Typography>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      onClick={() => navigate('/submit-project')}
                      sx={{
                        px: 5,
                        py: 1.8,
                        fontSize: '1.1rem',
                        borderRadius: 30,
                        animation: 'pulse-glow 3s ease-in-out infinite',
                      }}
                    >
                      {t('home.startProject')}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/showcase')}
                      sx={{
                        px: 4,
                        py: 1.8,
                        fontSize: '1.1rem',
                        borderRadius: 30,
                        borderColor: 'rgba(255,255,255,0.4)',
                        borderWidth: 2,
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        bgcolor: 'rgba(255,255,255,0.05)',
                        '&:hover': {
                          borderColor: 'white',
                          borderWidth: 2,
                          bgcolor: 'rgba(255,255,255,0.12)',
                        },
                      }}
                    >
                      {t('home.viewShowcase')}
                    </Button>
                  </Stack>
                </motion.div>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 2,
                        maxWidth: 340,
                      }}
                    >
                      {platforms.map((p, idx) => (
                        <motion.div
                          key={p.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 + idx * 0.1, duration: 0.5 }}
                          whileHover={{ scale: 1.1, y: -5 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              width: 95,
                              p: 2,
                              borderRadius: 3,
                              bgcolor: 'rgba(255,255,255,0.08)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              transition: 'all 0.3s ease',
                              cursor: 'default',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.15)',
                                border: `1px solid ${alpha(GREEN, 0.4)}`,
                              },
                            }}
                          >
                            {p.icon}
                            <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.9, fontWeight: 500 }}>
                              {p.name}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Stats Banner */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${GREEN} 0%, #2d9c1c 100%)`,
            py: 5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 4s linear infinite',
              pointerEvents: 'none',
            }}
          />
          <Container maxWidth="lg" sx={{ position: 'relative' }}>
            <Grid container spacing={4} justifyContent="center">
              {stats.map((stat, idx) => (
                <Grid size={{ xs: 12, sm: 4 }} key={stat.label}>
                  <FadeInSection delay={idx * 0.15}>
                    <Box sx={{ textAlign: 'center', color: 'white' }}>
                      <Box sx={{ mb: 1, opacity: 0.9 }}>{React.cloneElement(stat.icon, { sx: { fontSize: 36 } })}</Box>
                      <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '2.5rem' } }}>
                        <AnimatedNumber value={stat.value} suffix={stat.suffix} />
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </FadeInSection>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* All Platforms Banner */}
        <Box
          sx={{
            bgcolor: alpha(DARK_BLUE, 0.03),
            py: 3,
            borderBottom: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h6"
              align="center"
              sx={{
                color: DARK_BLUE,
                fontWeight: 600,
                letterSpacing: 1,
                opacity: 0.7,
              }}
            >
              {t('home.platformBanner')}
            </Typography>
          </Container>
        </Box>

        {/* Showcase */}
        {showcaseApps.length > 0 && (
          <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
            <Container maxWidth="lg">
              <FadeInSection>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {t('home.showcaseTitle')}
                  </Typography>
                  <Button
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/showcase')}
                    sx={{ color: GREEN, fontWeight: 600 }}
                  >
                    {t('home.showcaseViewAll')}
                  </Button>
                </Box>
              </FadeInSection>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  overflowX: 'auto',
                  pb: 2,
                  scrollSnapType: 'x mandatory',
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: alpha(DARK_BLUE, 0.2), borderRadius: 3 },
                }}
              >
                {showcaseApps.map((app) => {
                  const tr = getTranslation(app, i18n.language);
                  const cover =
                    app.media?.find((m) => m.type === 'image')?.url ||
                    app.screenshots?.[0] ||
                    '';
                  return (
                    <Card
                      key={app.id}
                      onClick={() => navigate('/showcase')}
                      sx={{
                        minWidth: { xs: 260, sm: 300 },
                        maxWidth: 320,
                        flex: '0 0 auto',
                        scrollSnapAlign: 'start',
                        cursor: 'pointer',
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: `0 12px 30px ${alpha(DARK_BLUE, 0.15)}`,
                        },
                      }}
                    >
                      {cover ? (
                        <CardMedia component="img" height="160" image={cover} alt={tr.name} />
                      ) : (
                        <Box
                          sx={{
                            height: 160,
                            background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PlayCircleOutlineIcon sx={{ fontSize: 56, color: 'white', opacity: 0.7 }} />
                        </Box>
                      )}
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {tr.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                          {tr.short_description}
                        </Typography>
                        {app.tags?.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {app.tags.map((tag) => (
                              <Chip key={tag} label={tag} size="small" sx={{ bgcolor: alpha(GREEN, 0.1), color: GREEN, fontWeight: 600, fontSize: '0.7rem' }} />
                            ))}
                          </Stack>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Container>
          </Box>
        )}

        {/* Features */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <FadeInSection>
            <Typography variant="h2" align="center" sx={{ mb: 1.5 }}>
              {(() => {
                const title = t('home.whyTitle');
                const brand = 'Booge IT';
                const idx = title.indexOf(brand);
                if (idx === -1) return title;
                return (
                  <>
                    {title.slice(0, idx)}
                    <Box
                      component="span"
                      sx={{
                        background: `linear-gradient(135deg, ${GREEN}, #2d9c1c)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {brand}
                    </Box>
                    {title.slice(idx + brand.length)}
                  </>
                );
              })()}
            </Typography>
          </FadeInSection>
          <FadeInSection delay={0.1}>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ mb: 8, maxWidth: 600, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}
            >
              {t('home.whyDesc')}
            </Typography>
          </FadeInSection>
          <StaggerContainer staggerDelay={0.15}>
            <Grid container spacing={4}>
              {features.map((f) => (
                <Grid size={{ xs: 12, md: 4 }} key={f.title} sx={{ display: 'flex' }}>
                  <StaggerItem style={{ display: 'flex', width: '100%' }}>
                    <GradientBorderCard
                      onClick={() => navigate(`/blog/${f.slug}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          textAlign: 'center',
                          p: 4,
                          border: 'none',
                          boxShadow: 'none',
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 3,
                              background: f.gradient,
                              color: 'white',
                              boxShadow: `0 8px 25px ${alpha(GREEN, 0.3)}`,
                            }}
                          >
                            {f.icon}
                          </Box>
                          <Typography variant="h5" sx={{ mt: 1, mb: 1.5, fontWeight: 700 }}>
                            {f.title}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            sx={{ lineHeight: 1.7, fontSize: '0.95rem' }}
                          >
                            {f.desc}
                          </Typography>
                        </CardContent>
                      </Card>
                    </GradientBorderCard>
                  </StaggerItem>
                </Grid>
              ))}
            </Grid>
          </StaggerContainer>
        </Container>

        {/* CTA */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${DARK_BLUE} 0%, #003d52 50%, #004a5e 100%)`,
            py: { xs: 8, md: 10 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <FloatingOrb size={300} color={GREEN} top={-80} right={-60} animationDelay="2s" />
          <FloatingOrb size={200} color="#4FC3F7" bottom={-60} left={-40} animationDelay="4s" />
          <Container maxWidth="sm" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <FadeInSection>
              <Typography variant="h3" sx={{ color: 'white', mb: 2, fontWeight: 700 }}>
                {t('home.ctaTitle')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 5, fontSize: '1.1rem', lineHeight: 1.7 }}>
                {t('home.ctaDesc')}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => navigate('/submit-project')}
                sx={{
                  px: 6,
                  py: 1.8,
                  fontSize: '1.15rem',
                  borderRadius: 30,
                }}
              >
                {t('home.startProject')}
              </Button>
            </FadeInSection>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
}
